import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CalendarPlus, Loader2, AlertCircle, X, ChevronRight, Users,
  Award, MapPin, RefreshCw, Trash2, CheckCircle2, AlertTriangle, FileText,
  MessagesSquare,
} from "lucide-react";
import {
  useGetChapterEventRequestsQuery,
  useCreateChapterEventRequestMutation,
  useDeleteChapterEventRequestMutation,
  type ChapterEventRequest,
  type ChapterEventRequestPayload,
} from "../api/chapterEventsApi";
import ChapterEventStatusBadge from "../components/ChapterEventStatusBadge";
import { AppendageChip } from "../components/ChapterEventAppendage";
import { CpdGuidelineButton } from "../components/CpdGuideline";

/**
 * The chapter's own CPD events, as its chairman raises them.
 *
 * Step 1 of the module: the request, with the CPD the chapter is asking for.
 * Everything after approval happens on the detail page — the register is built
 * there, and there is nothing to upload against a request nobody has cleared
 * yet, so this screen deliberately offers no attendance controls at all.
 */

function safeMsg(e: any): string {
  const errors = e?.data?.errors;
  if (errors) return Object.values(errors).flat().join(" ");
  return e?.data?.message || e?.error || e?.message || "Something went wrong.";
}

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });
}

function dateRange(from?: string | null, to?: string | null) {
  if (!from) return "—";
  if (!to || to === from) return fmtDate(from);
  return `${fmtDate(from)} – ${fmtDate(to)}`;
}

const EMPTY: ChapterEventRequestPayload = {
  title: "",
  theme: "",
  details: "",
  location: "",
  start_date: "",
  end_date: "",
  proposed_cpd_points: "",
};

// ── new request ──────────────────────────────────────────────────────────────

function NewRequestModal({
  chapters,
  onClose,
  onCreated,
}: {
  /** Only offered when he is acting for a second chapter; otherwise his own is implied. */
  chapters: { id: string; name: string }[];
  onClose: () => void;
  /** The id is what sends him straight on to the appendage, which is written there. */
  onCreated: (msg: string, id?: string) => void;
}) {
  const [form, setForm] = useState<ChapterEventRequestPayload>(EMPTY);
  const [error, setError] = useState("");
  const [create, { isLoading }] = useCreateChapterEventRequestMutation();

  function set<K extends keyof ChapterEventRequestPayload>(k: K, v: ChapterEventRequestPayload[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const res: any = await create({
        ...form,
        // An event on one day is the common case, so the end date follows the
        // start rather than making him type the same date twice.
        end_date: form.end_date || form.start_date,
        theme: form.theme || undefined,
        details: form.details || undefined,
      }).unwrap();
      onCreated(res?.message || "Event request submitted for approval.", res?.data?.id);
    } catch (err) {
      setError(safeMsg(err));
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm">
      <form
        onSubmit={submit}
        className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full max-w-2xl max-h-[94vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b sticky top-0 bg-white z-10">
          <div>
            <h3 className="font-bold text-gray-900">New Event Request</h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              The secretariat approves it before you can upload attendance.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="p-5 sm:p-6 space-y-4">
          {!!error && (
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {chapters.length > 1 && (
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-tight">Chapter</label>
              <select
                value={form.chapter_id ?? chapters[0].id}
                onChange={(e) => set("chapter_id", e.target.value)}
                className="w-full border px-3 py-2.5 rounded-lg text-sm bg-white"
              >
                {chapters.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-700 uppercase tracking-tight">Event title *</label>
            <input
              required
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="e.g. Chapter Technical Session on Measurement"
              className="w-full border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-700 uppercase tracking-tight">Theme</label>
            <input
              value={form.theme}
              onChange={(e) => set("theme", e.target.value)}
              className="w-full border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-700 uppercase tracking-tight">Venue *</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <input
                required
                value={form.location}
                onChange={(e) => set("location", e.target.value)}
                placeholder="Where it was held"
                className="w-full border pl-9 pr-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-tight">Start date *</label>
              <input
                type="date"
                required
                value={form.start_date}
                onChange={(e) => set("start_date", e.target.value)}
                className="w-full border px-3 py-2.5 rounded-lg text-sm bg-white"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-tight">End date</label>
              <input
                type="date"
                value={form.end_date}
                min={form.start_date || undefined}
                onChange={(e) => set("end_date", e.target.value)}
                className="w-full border px-3 py-2.5 rounded-lg text-sm bg-white"
              />
              <p className="text-[10px] text-gray-400">Leave blank for a one-day event.</p>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-tight">CPD points *</label>
              <input
                type="number"
                step="0.5"
                min="0.5"
                max="50"
                required
                value={form.proposed_cpd_points}
                onChange={(e) => set("proposed_cpd_points", e.target.value)}
                className="w-full border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500"
              />
              {/* Said plainly, because a chairman who does not know this reads a
                  reduction as an error rather than a decision. */}
              <p className="text-[10px] text-gray-400">
                What you are proposing. The secretariat may allow fewer.
              </p>
              {/* Right under the figure it answers: what a chapter event is
                  worth, and how much notice the committee expects for one. */}
              <CpdGuidelineButton variant="link" label="What is it worth?" />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-700 uppercase tracking-tight">
              Details for the secretariat
            </label>
            <textarea
              rows={4}
              value={form.details}
              onChange={(e) => set("details", e.target.value)}
              placeholder="Programme, speakers, duration — whatever justifies the CPD being asked for."
              className="w-full border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500"
            />
          </div>

          {/* The appendage is not asked for here on purpose: it is a document,
              not a field, and it is written on the request's own screen where
              there is room for it. Said plainly so that submitting this form is
              not mistaken for having finished. */}
          <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-blue-200 bg-blue-50 text-xs text-blue-800">
            <FileText className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Next you will be asked for the <b>appendage</b> — the Institute's template
              setting out the programme day by day, its technical papers and the resource
              persons behind them. The secretariat decides the CPD on it.
            </span>
          </div>
        </div>

        <div className="flex gap-3 px-5 sm:px-6 py-4 border-t sticky bottom-0 bg-white">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 py-2.5 border rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 py-2.5 bg-gray-900 rounded-lg text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-40 inline-flex items-center justify-center gap-2"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarPlus className="w-4 h-4" />}
            Submit for approval
          </button>
        </div>
      </form>
    </div>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────

const TABS: { key: string; label: string }[] = [
  { key: "", label: "All" },
  { key: "pending", label: "Awaiting approval" },
  // Second, right after pending: these are the ones the chapter is holding up,
  // and the whole point of the status is that they do not sit unnoticed among
  // the requests genuinely waiting on the secretariat.
  { key: "queried", label: "Needs your answer" },
  { key: "approved", label: "Approved" },
  { key: "attendance_submitted", label: "Awaiting allocation" },
  { key: "allocated", label: "CPD allocated" },
  { key: "rejected", label: "Rejected" },
];

export default function ChapterEventsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [ok, setOk] = useState("");
  const [error, setError] = useState("");

  const { data, isLoading, isFetching, error: loadError, refetch } =
    useGetChapterEventRequestsQuery(tab ? { status: tab } : undefined);

  const [remove, { isLoading: removing }] = useDeleteChapterEventRequestMutation();

  const notALeader = (loadError as any)?.status === 403;
  const rows: ChapterEventRequest[] = data?.data ?? [];
  // Memoised, not just `data?.acting_for ?? []`: the fallback is a fresh array on
  // every render, which the chapter list below depends on.
  const actingFor = useMemo(() => data?.acting_for ?? [], [data]);

  // The chapter picker on the new-request form only appears when there is a
  // choice to make.
  const chapters = useMemo(() => {
    const own = data?.chapter ? [{ id: data.chapter.id, name: data.chapter.name }] : [];
    return [...own, ...actingFor];
  }, [data?.chapter, actingFor]);

  const withdraw = async (r: ChapterEventRequest) => {
    if (!window.confirm(`Withdraw the request for "${r.title}"?`)) return;
    setError(""); setOk("");
    try {
      const res: any = await remove(r.id).unwrap();
      setOk(res?.message || "Request withdrawn.");
    } catch (e) {
      setError(safeMsg(e));
    }
  };

  if (notALeader) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-sm">
        <div className="px-4 py-3 rounded-lg border border-amber-200 bg-amber-50 text-sm font-semibold text-amber-800">
          {(loadError as any)?.data?.message ||
            "You do not hold office in any active chapter."}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 bg-white rounded-lg shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
            {data?.chapter?.name ? `${data.chapter.name} Chapter Events` : "Chapter Events"}
          </h2>
          <p className="text-xs text-gray-500 font-medium mt-1 max-w-2xl">
            Raise an event with the CPD you are proposing. Once the secretariat
            approves it, upload the attendance and submit it for CPD allocation.
          </p>
          {!!actingFor.length && (
            <p className="text-[11px] text-gray-400 font-medium mt-1">
              Also acting for: {actingFor.map((c) => c.name).join(", ")}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* On the header too, not only inside the form: a chairman checking
              what the committee allows should not have to start a request to
              find out. */}
          <CpdGuidelineButton />
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-2.5 rounded-lg border text-gray-500 hover:bg-gray-50 disabled:opacity-40"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={() => { setShowNew(true); setOk(""); setError(""); }}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800"
          >
            <CalendarPlus className="w-4 h-4" />
            New Event Request
          </button>
        </div>
      </div>

      {!!ok && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-green-200 bg-green-50 text-sm text-green-700">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{ok}</span>
        </div>
      )}
      {!!error && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex gap-2 overflow-x-auto pb-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide whitespace-nowrap border transition-colors ${
              tab === t.key
                ? "bg-gray-900 text-white border-gray-900"
                : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="py-16 flex flex-col items-center gap-2 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="text-xs font-medium">Loading your events…</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center space-y-2 border-2 border-dashed border-gray-200 rounded-2xl">
          <p className="text-sm font-bold text-gray-500">No event requests yet</p>
          <p className="text-xs text-gray-400">
            Raise one for any chapter meeting or seminar your members should earn CPD for.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div
              key={r.id}
              className="border border-gray-200 rounded-2xl p-4 hover:border-gray-900 transition-colors"
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="min-w-0 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-bold text-gray-900 break-words">{r.title}</span>
                    <ChapterEventStatusBadge status={r.status} />
                    {/* Only while it is still his to write. After a decision the
                        chip is history rather than a thing to act on, and the
                        document itself is on the detail screen either way. */}
                    {r.can_edit && <AppendageChip complete={r.appendage_complete} />}
                    {/* Per person, not per chapter: the chairman and his
                        secretary each have their own read mark, so this is
                        what THIS reader has not seen. */}
                    {!!r.unread_count && r.unread_count > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide bg-amber-400 text-amber-950">
                        <MessagesSquare className="w-3 h-3" />
                        {r.unread_count} new
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {r.location}
                    </span>
                    <span>{dateRange(r.start_date, r.end_date)}</span>
                    {actingFor.length > 0 && r.chapter && (
                      <span className="font-semibold text-gray-600">{r.chapter.name}</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="inline-flex items-center gap-1">
                      <Award className="w-3 h-3" />
                      {/* The ask and the allowance, side by side, whenever they
                          differ — a reduction the chairman does not notice is a
                          complaint later. */}
                      {r.cpd_was_reduced ? (
                        <>
                          <span className="line-through text-gray-400">{r.proposed_cpd_points}</span>
                          <span className="font-bold text-gray-900">{r.effective_cpd_points} pts</span>
                          <span className="text-amber-600 font-semibold">(reduced)</span>
                        </>
                      ) : (
                        <span className="font-bold text-gray-900">{r.effective_cpd_points} pts</span>
                      )}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {r.attendees_count} on register
                      {r.attendees_count > 0 && r.member_attendees_count !== r.attendees_count && (
                        <span className="text-gray-400">
                          ({r.member_attendees_count} member{r.member_attendees_count === 1 ? "" : "s"})
                        </span>
                      )}
                    </span>
                    {r.unmatched_count > 0 && r.status !== "rejected" && (
                      // The chairman's own to-do: these earn nothing until he
                      // corrects them, and after allocation it is the only thing
                      // left for him to act on.
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 text-[10px] font-bold uppercase tracking-wide">
                        <AlertTriangle className="w-3 h-3" /> {r.unmatched_count} need a match
                      </span>
                    )}
                  </div>

                  {r.status === "rejected" && !!r.review_notes && (
                    <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-2.5 py-1.5">
                      <span className="font-bold">Rejected:</span> {r.review_notes}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {r.can_edit && (
                    <button
                      onClick={() => withdraw(r)}
                      disabled={removing}
                      className="p-2 rounded-lg border text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40"
                      title="Withdraw"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  <Link
                    to={`/chapter/events/${r.id}`}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-gray-900 text-white text-[11px] font-bold uppercase tracking-wide hover:bg-gray-800"
                  >
                    {r.status === "approved"
                      ? "Attendance"
                      : r.status === "allocated"
                        ? "CPD details"
                        : "Open"}
                    <ChevronRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <NewRequestModal
          chapters={chapters}
          onClose={() => setShowNew(false)}
          onCreated={(msg, id) => {
            setShowNew(false);
            setOk(msg);
            // Straight on to the appendage rather than back to a list row that
            // gives no hint there is a document still to write.
            if (id) navigate(`/chapter/events/${id}`);
          }}
        />
      )}
    </div>
  );
}
